import { Link } from "react-router-dom";
import { Phone, ArrowRight, PhoneCall, ClipboardCheck, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import ServiceCard from "@/components/ServiceCard";
import { PHONE, PHONE_LINK, PLUMBING_SERVICES, BUSINESS_NAME, AREA_SERVED_SCHEMA, SOCIAL_PROFILES } from "@/lib/constants";

const Services = () => {
  const baseUrl = "https://truamericanplumbing.com";

  const serviceSchema = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: baseUrl },
        { "@type": "ListItem", position: 2, name: "Plumbing Services", item: `${baseUrl}/services` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Plumbing",
      provider: {
        "@type": "Plumber",
        name: BUSINESS_NAME,
        "@id": `${baseUrl}/#organization`,
        sameAs: SOCIAL_PROFILES,
      },
      areaServed: AREA_SERVED_SCHEMA,
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Plumbing Services",
        itemListElement: PLUMBING_SERVICES.map((s, i) => ({
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: s.title,
            description: s.shortDesc,
            url: `${baseUrl}/services/${s.slug}`,
          },
        })),
      },
    },
  ];

  return (
    <>
      <SEO
        title="Plumbing Services in Tolleson, AZ"
        description="Complete plumbing services in Tolleson and the West Valley. Emergency repairs, drain cleaning, water heaters, leak detection, and more. Call (480) 322-2567."
        canonicalUrl="/services/"
        schema={serviceSchema}
      />

      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-muted/50 to-background">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Professional{" "}
              <span className="text-gradient-copper">Plumbing Services</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-4">
              From emergency repairs to routine maintenance, our licensed plumbers
              deliver quality workmanship for homes and businesses throughout
              Tolleson and the West Valley.
            </p>
            <p className="text-base text-foreground/70 mb-8 max-w-2xl mx-auto leading-relaxed">
              Arizona homes face unique plumbing challenges that other parts of the
              country rarely see. Extreme hard water causes mineral buildup inside
              pipes and fixtures, desert heat shortens the lifespan of water heaters,
              and shifting soil conditions can lead to foundation-level slab leaks.
              A licensed plumber familiar with these local conditions can diagnose
              issues faster and recommend solutions that last.
            </p>
            <a href={PHONE_LINK}>
              <Button variant="hero" size="xl" className="gap-2">
                <Phone className="h-5 w-5" />
                Call {PHONE}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-4">
            How It <span className="text-gradient-copper">Works</span>
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            Getting professional plumbing help in the West Valley is straightforward.
            Here's what to expect when you contact us for service.
          </p>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 gradient-copper rounded-xl flex items-center justify-center mx-auto mb-4">
                <PhoneCall className="h-8 w-8 text-primary-foreground" />
              </div>
              <h3 className="text-xl font-bold mb-2">1. Call or Book Online</h3>
              <p className="text-muted-foreground text-sm">
                Reach us by phone or through our contact form. Our 24/7 scheduling
                assistant can book your appointment even after business hours.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 gradient-copper rounded-xl flex items-center justify-center mx-auto mb-4">
                <ClipboardCheck className="h-8 w-8 text-primary-foreground" />
              </div>
              <h3 className="text-xl font-bold mb-2">2. Diagnosis & Quote</h3>
              <p className="text-muted-foreground text-sm">
                A licensed plumber arrives at your home, inspects the problem, and
                provides an upfront quote before any work begins — no surprises.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 gradient-copper rounded-xl flex items-center justify-center mx-auto mb-4">
                <Wrench className="h-8 w-8 text-primary-foreground" />
              </div>
              <h3 className="text-xl font-bold mb-2">3. Repair & Verify</h3>
              <p className="text-muted-foreground text-sm">
                We complete the repair using quality materials, test everything, and
                walk you through what was done so you understand the fix.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-24">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-4">
            Our <span className="text-gradient-copper">Services</span>
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            We offer a full range of residential and light commercial plumbing
            services. Click any service below to learn more about how we can help.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PLUMBING_SERVICES.map((service) => (
              <ServiceCard key={service.slug} {...service} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Services;
